// Tekenen van bestemmingen: bakkerij, klanten met deadline-ring, richtingpijl, route,
// pijlen aan de schermrand voor doelen buiten beeld, en de nachtelijke lichtlaag.
// Leest alleen this.targets en this.activeTarget (berekend in BezorgerGame.updateTargets).
import { planRoute } from '../game/route';
import { urgencyOf, urgencyStyle } from '../game/urgency';

/** Kleur, patroon en symbool van een doel; kleurenblindveilig als de instelling aan staat. */
const styleOf = (target, colorblind) => urgencyStyle(urgencyOf(target), colorblind);

export function urgencyColor(target, colorblind = false) {
  return styleOf(target, colorblind).color;
}

export class RenderTargetsMixin {
  /** Is dit punt dichtbij de scooter? Voor labels en wolkjes die alleen vlakbij iets te vertellen hebben. */
  near(x, y, r) {
    const dx = this.scooter.x - x;
    const dy = this.scooter.y - y;
    return dx * dx + dy * dy < r * r;
  }

  /** Is een wereldpunt (met marge) zichtbaar in het huidige beeld? */
  inView(x, y, margin = 0) {
    const v = this.view;
    return !v || (x >= v.x0 - margin && x <= v.x1 + margin && y >= v.y0 - margin && y <= v.y1 + margin);
  }

  renderRouteLine(ctx) {
    const dest = this.activeTarget;
    if (!dest) return;
    const s = this.scooter;
    const style = styleOf(dest, this.save.settings.colorblind);
    // Route over het raster, via de brug als de gracht ertussen ligt. Herberekenen kost bijna niets.
    const route = planRoute({ x: s.x, y: s.y }, { x: dest.x, y: dest.y });

    ctx.save();
    ctx.globalAlpha = 0.55;
    ctx.strokeStyle = style.color;
    ctx.lineWidth = style.routeWidth;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.setLineDash(style.routeDash);
    ctx.lineDashOffset = -this.time * 40;
    ctx.beginPath();
    ctx.moveTo(route[0].x, route[0].y);
    for (const p of route.slice(1)) ctx.lineTo(p.x, p.y);
    ctx.stroke();
    ctx.restore();
  }

  renderTargets(ctx) {
    if (!this.targets?.length) return;
    const pulse = Math.sin(this.time * 6) * 4;
    for (const t of this.targets) {
      if (!this.inView(t.x, t.y, 120)) continue;
      const style = styleOf(t, this.save.settings.colorblind);
      const color = style.color;
      const isActive = t === this.activeTarget;

      ctx.save();
      // Zachte vulling en pulserende rand
      ctx.globalAlpha = 0.22;
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(t.x, t.y, t.r + (isActive ? pulse : 0), 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 1;
      ctx.lineWidth = 3;
      ctx.strokeStyle = color;
      ctx.setLineDash(style.ringDash);
      ctx.beginPath();
      ctx.arc(t.x, t.y, t.r + (isActive ? pulse : 0), 0, Math.PI * 2);
      ctx.stroke();
      ctx.setLineDash([]);

      // Deadline-ring: het gekleurde deel is de resterende tijd
      if (t.kind === 'customer') {
        ctx.lineWidth = 7;
        ctx.strokeStyle = 'rgba(15, 23, 42, 0.55)';
        ctx.beginPath();
        ctx.arc(t.x, t.y, t.r + 14, 0, Math.PI * 2);
        ctx.stroke();
        ctx.strokeStyle = color;
        ctx.lineCap = 'round';
        const frac = Math.max(0, Math.min(1, t.fraction));
        if (frac > 0) {
          ctx.beginPath();
          ctx.arc(t.x, t.y, t.r + 14, -Math.PI / 2, -Math.PI / 2 + frac * Math.PI * 2);
          ctx.stroke();
        }
      }

      // Voortgang van inladen/afleveren terwijl je stilstaat
      if (t.dwell > 0) {
        ctx.lineWidth = 6;
        ctx.strokeStyle = '#ffffff';
        ctx.beginPath();
        ctx.arc(t.x, t.y, t.r + 26, -Math.PI / 2, -Math.PI / 2 + Math.min(1, t.dwell) * Math.PI * 2);
        ctx.stroke();
      }

      // Icoon in het midden
      ctx.font = '26px system-ui, "Apple Color Emoji", "Segoe UI Emoji", sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(t.icon, t.x, t.y + 1);

      // Naam en tijd
      const timeLabel = t.kind === 'bakery' ? t.label : `${t.label} · ${Math.max(0, Math.ceil(t.secondsLeft))}s`;
      const label = style.glyph ? `${style.glyph} ${timeLabel}` : timeLabel;
      ctx.font = '700 15px "Plus Jakarta Sans Variable", system-ui, sans-serif';
      ctx.lineWidth = 4;
      ctx.strokeStyle = 'rgba(15, 23, 42, 0.85)';
      ctx.fillStyle = '#ffffff';
      ctx.strokeText(label, t.x, t.y - t.r - 34);
      ctx.fillText(label, t.x, t.y - t.r - 34);
      ctx.restore();
    }
  }

  /** Pijl die rond de scooter naar het actieve doel wijst. */
  renderGuideArrow(ctx) {
    const dest = this.activeTarget;
    if (!dest) return;
    const s = this.scooter;
    const dx = dest.x - s.x;
    const dy = dest.y - s.y;
    const dist = Math.hypot(dx, dy);
    if (dist < dest.r + 30) return;
    const angle = Math.atan2(dy, dx);
    const style = styleOf(dest, this.save.settings.colorblind);
    const color = style.color;
    const bob = Math.sin(this.time * 8) * 3;

    ctx.save();
    ctx.translate(s.x + Math.cos(angle) * 58, s.y + Math.sin(angle) * 58);
    ctx.rotate(angle);
    ctx.fillStyle = color;
    ctx.strokeStyle = 'rgba(15, 23, 42, 0.8)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(16 + bob, 0);
    ctx.lineTo(-9, -10);
    ctx.lineTo(-3, 0);
    ctx.lineTo(-9, 10);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    if (style.glyph) {
      // Het symbool staat rechtop naast de pijl
      ctx.rotate(-angle);
      ctx.font = '700 16px "Plus Jakarta Sans Variable", system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.lineWidth = 3;
      ctx.strokeStyle = 'rgba(15, 23, 42, 0.9)';
      ctx.strokeText(style.glyph, 0, -22);
      ctx.fillText(style.glyph, 0, -22);
    }
    ctx.restore();
  }

  /** Doelen buiten beeld: een rondje met icoon en afstand aan de schermrand. In schermcoördinaten. */
  renderEdgeMarkers(ctx, w, h) {
    if (!this.targets?.length) return;
    const z = this.camera.zoom;
    const landscape = w > h;
    const insetTop = this.safeTop ?? 80;
    // Rechtop houden de knoppen onderin ruimte bezet; liggend staan ze in de hoeken en blijft de onderrand vrij.
    const insetBottom = landscape ? 30 : this.safeBottom ?? 120;
    const pad = 30;
    const cx = w / 2;
    const cy = (insetTop + h - insetBottom) / 2;
    const halfW = w / 2 - pad;
    const halfH = (h - insetBottom - insetTop) / 2 - 6;

    // Stap 1: waar komt elk doel op de rand van het kader?
    const markers = [];
    for (const t of this.targets) {
      const sx = (t.x - this.camera.x) * z + w / 2;
      const sy = (t.y - this.camera.y) * z + h / 2;
      if (sx > pad && sx < w - pad && sy > insetTop && sy < h - insetBottom) continue;
      const dx = sx - cx;
      const dy = sy - cy;
      const scale = Math.min(halfW / Math.abs(dx || 1e-6), halfH / Math.abs(dy || 1e-6));
      markers.push({ t, a: Math.atan2(dy, dx), x: cx + dx * scale, y: cy + dy * scale });
    }

    // Stap 2: markers die op elkaar zitten schuiven uit elkaar, binnen het kader
    const MIN = 52;
    for (let pass = 0; pass < 6; pass++) {
      for (let i = 0; i < markers.length; i++) {
        for (let j = i + 1; j < markers.length; j++) {
          const A = markers[i];
          const B = markers[j];
          let dx = B.x - A.x;
          let dy = B.y - A.y;
          let d = Math.hypot(dx, dy);
          if (d >= MIN) continue;
          if (d < 0.01) {
            dx = 0;
            dy = 1;
            d = 1;
          }
          const push = (MIN - d) / 2;
          A.x -= (dx / d) * push;
          A.y -= (dy / d) * push;
          B.x += (dx / d) * push;
          B.y += (dy / d) * push;
        }
      }
      for (const m of markers) {
        m.x = Math.max(cx - halfW, Math.min(cx + halfW, m.x));
        m.y = Math.max(cy - halfH, Math.min(cy + halfH, m.y));
      }
    }

    // Stap 3: tekenen
    for (const m of markers) {
      const t = m.t;
      const style = styleOf(t, this.save.settings.colorblind);
      const color = style.color;
      const meters = Math.round(Math.hypot(t.x - this.scooter.x, t.y - this.scooter.y) * 0.25);
      ctx.save();
      ctx.translate(m.x, m.y);
      // Puntje dat naar het doel wijst
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.moveTo(Math.cos(m.a) * 30, Math.sin(m.a) * 30);
      ctx.lineTo(Math.cos(m.a + 0.5) * 20, Math.sin(m.a + 0.5) * 20);
      ctx.lineTo(Math.cos(m.a - 0.5) * 20, Math.sin(m.a - 0.5) * 20);
      ctx.closePath();
      ctx.fill();
      // Rondje met icoon
      ctx.fillStyle = 'rgba(15, 23, 42, 0.88)';
      ctx.strokeStyle = color;
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.arc(0, 0, 21, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      ctx.font = '19px system-ui, "Apple Color Emoji", "Segoe UI Emoji", sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(t.icon, 0, 1);
      ctx.font = '700 12px "Plus Jakarta Sans Variable", system-ui, sans-serif';
      ctx.fillStyle = '#ffffff';
      ctx.strokeStyle = 'rgba(15, 23, 42, 0.9)';
      ctx.lineWidth = 3;
      const distance = style.glyph ? `${style.glyph} ${meters} m` : `${meters} m`;
      ctx.strokeText(distance, 0, 33);
      ctx.fillText(distance, 0, 33);
      ctx.restore();
    }
  }

  /**
   * Nacht: een donkere laag met lichtcirkels, getekend op een aparte canvas.
   * (Vroeger werden de lichtcirkels uit de hele scène geknipt, waardoor lantaarns zwarte gaten werden.)
   */
  renderNightLayer(ctx, w, h) {
    if (!this.nightCanvas) this.nightCanvas = document.createElement('canvas');
    const nc = this.nightCanvas;
    const pw = this.canvas.width;
    const ph = this.canvas.height;
    if (nc.width !== pw || nc.height !== ph) {
      nc.width = pw;
      nc.height = ph;
    }
    const n = nc.getContext('2d');
    const z = this.camera.zoom;
    n.setTransform(1, 0, 0, 1, 0, 0);
    n.globalCompositeOperation = 'source-over';
    n.clearRect(0, 0, pw, ph);
    n.fillStyle = 'rgba(10, 15, 28, 0.62)';
    n.fillRect(0, 0, pw, ph);

    n.setTransform(this.dpr * z, 0, 0, this.dpr * z, this.dpr * (w / 2 - this.camera.x * z), this.dpr * (h / 2 - this.camera.y * z));
    n.globalCompositeOperation = 'destination-out';
    const hole = (x, y, r, strength) => {
      if (!this.inView(x, y, r)) return;
      const g = n.createRadialGradient(x, y, r * 0.1, x, y, r);
      g.addColorStop(0, `rgba(0,0,0,${strength})`);
      g.addColorStop(0.6, `rgba(0,0,0,${strength * 0.5})`);
      g.addColorStop(1, 'rgba(0,0,0,0)');
      n.fillStyle = g;
      n.beginPath();
      n.arc(x, y, r, 0, Math.PI * 2);
      n.fill();
    };
    for (const lamp of this.map.streetLamps) hole(lamp.x, lamp.y, 110, 0.85);
    const bk = this.map.bakery;
    hole(bk.x + bk.w / 2, bk.y + bk.h, 170, 0.9);
    for (const car of this.map.cars ?? []) {
      hole(car.x + Math.cos(car.angle) * (car.length / 2 + 35), car.y + Math.sin(car.angle) * (car.length / 2 + 35), 95, 0.75);
    }
    for (const inter of this.map.intersections ?? []) if (inter.hasTrafficLight) hole(inter.x, inter.y, 110, 0.6);
    // Koplamp van de speler en de doelen blijven goed zichtbaar
    const s = this.scooter;
    hole(s.x + Math.cos(s.angle) * 70, s.y + Math.sin(s.angle) * 70, 150, 0.9);
    hole(s.x, s.y, 70, 0.9);
    for (const t of this.targets ?? []) hole(t.x, t.y, t.r + 50, 0.8);

    ctx.drawImage(nc, 0, 0, w, h);
  }
}
